import { Channel, ChannelData, StreamChat } from 'stream-chat';
import { ChatContextValue } from 'stream-chat-react';

import MessagesClient from 'src/clients/MessagesClient';
import {
  MessageChannelEntity,
  MessagesActionTypes,
} from 'src/store/messages/types';
import { AppThunkAction } from 'src/store/reduxTypes';
import { GetCurrentUser } from 'src/store/users/reducers';
import { removeDuplicates } from 'src/utils/array';

const startInitializeClientAction = () => ({
  type: MessagesActionTypes.INITIALIZE_CLIENT_START,
});

const getStreamClientSetAction = (currentUserId?: string) => ({
  type: MessagesActionTypes.INITIALIZE_CLIENT_DONE,
  currentUserId,
});

const initializeClientFailedAction = () => ({
  type: MessagesActionTypes.INITIALIZE_CLIENT_FAILED,
});

export const initializeClientAction =
  (client: StreamChat): AppThunkAction =>
  async (dispatch, getState) => {
    const {
      user: { id: currentUserId, isClient },
      messages: messagesState,
      users: usersState,
    } = getState();

    const currentUser = GetCurrentUser({
      isClient,
      currentUserId,
      usersState,
    });

    if (
      messagesState.clientInitialized ||
      messagesState.clientInitializing ||
      !currentUser
    ) {
      // do no initialize client if it is already initialized or initializing
      // or if current user is not present yet
      return;
    }
    dispatch(startInitializeClientAction());

    let token = '';
    try {
      token = await MessagesClient.GetUserToken();
    } catch (error) {
      console.error('failed to get chat user token', error);
      dispatch(initializeClientFailedAction());
      return;
    }

    const getStreamId = currentUser.getstreamId;
    if (!token || !getStreamId) {
      dispatch(initializeClientFailedAction());
      return;
    }

    try {
      const result = await client.connectUser(
        {
          id: getStreamId,
          name: `${currentUser.fields.givenName} ${currentUser.fields.familyName}`,
          image: currentUser.fields.avatarImageUrl,
          fallbackColor: currentUser.fields.fallbackColor,
        },
        token,
      );
      if (result && result.me) {
        const { unread_count } = result.me;
        let messagingUnreadCount: number = 0;
        if (unread_count > 0) {
          // We now have two types of GetStream channels (Messaging and Internal). If unreadCount is greater than 0
          // We should use the unreadCount api to ensure we're only counting the messaging unread count from the channel types
          try {
            const response = await client.getUnreadCount(getStreamId);
            const channelTypes = response.channel_type;
            channelTypes.forEach((channelType) => {
              if (channelType.channel_type === 'messaging') {
                messagingUnreadCount = channelType.unread_count;
              }
            });
          } catch (countError) {
            console.error('failed to get unread count', countError);
          }
        }
        // For IU viewers, skip the raw seed: GetStream's `getUnreadCount` includes
        // IU-authored messages, so dispatching it here causes a brief count spike
        // on refresh until `useUserNotification` derives the suppressed total.
        // The hook's seed effect will dispatch the correct derived count once
        // `clientInitialized` flips and chatClient becomes available.
        if (isClient) {
          dispatch(setTotalUnreadMessages(messagingUnreadCount));
        }
      }
    } catch (ex) {
      console.error('failed to connect user with chat', ex);
      dispatch(initializeClientFailedAction());
      return;
    }

    dispatch(getStreamClientSetAction(getStreamId));

    try {
      await MessagesClient.SetUserRole(currentUser.id);
    } catch (error) {
      console.error('failed to set user role', error);
    }
  };

export const createChannel =
  (
    client: StreamChat,
    newChannelData: ChannelData,
    setActiveChannel: ChatContextValue['setActiveChannel'],
  ): AppThunkAction =>
  async (dispatch) => {
    function done(channel: Channel) {
      return {
        type: MessagesActionTypes.SET_CREATED_CHANNEL,
        channelId: channel.id,
      };
    }

    const channelId =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);

    const newChannel = client.channel('messaging', channelId, newChannelData);
    await newChannel.watch();
    setActiveChannel(newChannel);

    dispatch(done(newChannel));
  };

export const setTotalUnreadMessages = (totalUnreadMessagesCount: number) => ({
  type: MessagesActionTypes.SET_UNREAD_MESSAGES_COUNT,
  payload: totalUnreadMessagesCount,
});

export const addDeletedChannel = (channelID: string) => ({
  type: MessagesActionTypes.ADD_DELETED_CHANNEL,
  payload: channelID,
});

export const setNoLoadedChannel = (notLoaded: boolean) => ({
  type: MessagesActionTypes.SET_NO_CHANNEL_LOADED,
  payload: notLoaded,
});

export const setLoadingChannelError = (error: string) => ({
  type: MessagesActionTypes.SET_LOADING_CHANNEL_ERROR,
  payload: error,
});

export const createInternalChannel =
  (
    client: StreamChat,
    channelItem: MessageChannelEntity,
    portalConfig: any,
    setActiveChannel: ChatContextValue['setActiveChannel'],
    singleClientId?: string,
  ): AppThunkAction =>
  async (dispatch, getState) => {
    const messagesState = getState().messages;
    if (!messagesState.currentUserId) {
      return;
    }
    const mergedMemberIDs = channelItem.fields.getstreamMembers.concat([
      messagesState.currentUserId,
    ]);

    // const formattedMemberIDs = channelItem.fields.members
    //   .map(memberID => getFullUserIdFromUserId(portalConfig, memberID))
    //   .concat([messagesState.currentUserId]);
    const channelMetaData: ChannelData = {
      members: mergedMemberIDs,
      name: channelItem.fields.name,
      userPoolId: portalConfig.portalHeader,
      companyID: channelItem.fields.companyID,
      membershipType: channelItem.fields.membersType,
    };
    if (singleClientId) {
      channelMetaData.singleClientId = singleClientId;
    }
    // add group metadata tag if no clientId or companyId is present
    if (
      (!singleClientId && !channelItem.fields.companyID) ||
      channelItem.fields.membersType === 'group'
    ) {
      channelMetaData.isGroupType = true;
    }
    if (channelItem.fields.members.length > 0) {
      await MessagesClient.SetupChannelMembers(
        removeDuplicates(channelItem.fields.members),
      );
    }
    dispatch(createChannel(client, channelMetaData, setActiveChannel));
  };

export const setTotalUnreadMessagesByCompany = (
  companyId: string,
  totalUnreadMessagesCount: number,
) => ({
  type: MessagesActionTypes.SET_TOTAL_UNREAD_MESSAGES_BY_COMPANY,
  payload: { companyId, totalUnreadMessagesCount },
});

export const incrementTotalUnreadMessagesByCompany = (
  companyId: string,
  incrementCount: number,
) => ({
  type: MessagesActionTypes.INCREMENT_TOTAL_UNREAD_MESSAGES_BY_COMPANY,
  payload: { companyId, incrementCount },
});

export const setChannelIdToCompanyId = (
  channelId: string,
  companyId: string,
) => ({
  type: MessagesActionTypes.SET_CHANNEL_ID_TO_COMPANY_ID,
  payload: { channelId, companyId },
});
